import styled from '@emotion/styled';
import { PORT_NODE_CONNECTED, PORT_NODE_DISCONNECTED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useFlowPort } from 'modules/Editor/components/FlowCore';
import { getFlowModeWidgetSelector } from 'modules/Editor/components/FlowCore/FlowCore.helpers';
import { useGetWidgetDataRect } from 'modules/Editor/components/FlowCore/components/PortStepsGrid.hooks';
import { useAppDispatch } from 'modules/Editor/store';
import { WidgetActionState, setWidgetActionState } from 'modules/Editor/store/widgetControlSlice';
import { DragHandler } from 'modules/common/components/DragHandler';
import { RefObject, createRef, useCallback, useEffect, useRef, useState } from 'react';
import { OnDrag, OnDragEnd, OnDragStart } from 'react-moveable';
import { PortTypes } from 'types/flowCore.types';
import { GroupTargetData } from 'types/flowCore.types';
import { WidgetType } from 'types/widget.types';
import {
  detectNearestGroupElement,
  detectPortElement,
  detectWidgetElementWithProximity,
  getDOMMatrix,
  getParentWidgetElement,
} from 'utils/dom';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { useEditor } from 'widgets/sdk';

import { DEFAULT_CUSTOM_HANDLE_BORDER_RADIUS, DEFAULT_DELTA_POS, HANDLER_PADDING } from '../LineWidget.config';
import {
  calculateTargetPoint,
  calculateWidgetRect,
  getClosestPosByAngle,
  getPositionMiddleOfSide,
} from '../LineWidget.helpers';
import { DeltaPos, Pos } from '../LineWidget.types';
import { DragHandlerProps } from '../LineWidget.types';

interface HandleProps {
  left: number;
  top: number;
  width: number;
  height: number;
  border: string;
  borderRadius: string;
}

const HandleWrapper = styled.div`
  position: absolute;
  background-color: transparent;
  z-index: var(--vg-zIndices-lineWidgetHandle);
`;
const Handle = styled.div<HandleProps>`
  background-color: #fff;
  left: ${(props) => props.left}px;
  top: ${(props) => props.top}px;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  border: ${(props) => props.border};
  border-radius: 50%;

  &:focus {
    outline: unset;
    box-shadow: var(--vg-shadows-outline);
  }

  &:hover {
    background: var(--vg-colors-upgrade-500);
  }
`;

/**
 * Stepped Line Handler.
 * TODO: custom drag behavior if we want user to be able to drag each step part of the line.
 * Note: Right now, identical to Straight.tsx line handler. Change later when we need to.
 * @param param0
 * @returns
 */
export const Curved = ({
  widgetId,
  leftPx,
  topPx,
  startPort,
  endPort,
  posList = [],
  zoom,
  handlerSize,
  activeHandlerIndex,
  setFocus,
  setDelta,
  updateData,
  setRealTimeStartRect,
  setRealTimeEndRect,
  setRealTimeStartPort,
  setRealTimeEndPort,
  setIsUseRealTimeStartRect,
  setIsUseRealTimeEndRect,
}: DragHandlerProps) => {
  const targetElmRefs = useRef<RefObject<HTMLDivElement>[]>(posList.map(() => createRef()));
  const targetHandlerElmRefs = useRef<RefObject<HTMLDivElement>[]>(posList.map(() => createRef()));
  const [, updateState] = useState<object>();
  const forceUpdate = useCallback(() => updateState({}), []);
  const lastDelta = useRef<DeltaPos>(DEFAULT_DELTA_POS);
  const { renderPort, clearPort, getSelectedPort } = useFlowPort();
  const { boundingBox } = useEditor();

  const { getWidgetBoundingRectData } = useGetWidgetDataRect();
  const [allGroupWidgets, setAllGroupWidgets] = useState<{ widgetId: string; widgetData: GroupWidgetData }[]>([]);

  // Real-time group element active when dragging line end to group widget
  const [targetDragGroupElement, setTargetDragGroupElement] = useState<GroupTargetData | null>(null);
  const { getRectOfGroupFromWidgetIdAsRect, getWidgetDataOfAllGroups } = useGetWidgetDataRect();
  const dispatch = useAppDispatch();

  // Return the current target index by using the element.
  const getTargetIndex = useCallback(
    (element: HTMLElement | SVGElement): number => {
      return targetHandlerElmRefs.current.findIndex((targetElm) => {
        return targetElm.current === element;
      });
    },
    [targetHandlerElmRefs],
  );

  // Return the string of current direction using the handler index
  const getCurrentDirection = useCallback(
    (index: number): string => (index === 0 ? 'start' : index === posList.length - 1 ? 'end' : ''),
    [posList],
  );

  const handleFocus = (activeHandlerIndex: number) => {
    const targetElement = targetElmRefs.current[activeHandlerIndex]?.current as HTMLDivElement;
    if (!targetElement) return;

    if (activeHandlerIndex >= 0) {
      targetElement.focus();
    } else {
      targetElement.blur();
    }
  };

  /**
   * Custom handler events
   */
  const handleDragStart = useCallback(
    (e: OnDragStart) => {
      const targetIndex = getTargetIndex(e.target);
      setFocus({ index: targetIndex });
      handleFocus(targetIndex);
      dispatch(setWidgetActionState(WidgetActionState.Drag));

      if (setRealTimeStartRect && setRealTimeEndRect && setRealTimeStartPort && setRealTimeEndPort) {
        setRealTimeStartRect(null);
        setRealTimeEndRect(null);
        setRealTimeStartPort(null);
        setRealTimeEndPort(null);
      }
      if (targetIndex === 0 && setIsUseRealTimeStartRect) {
        setIsUseRealTimeStartRect(true);
      } else if (setIsUseRealTimeEndRect) {
        setIsUseRealTimeEndRect(true);
      }

      setAllGroupWidgets(getWidgetDataOfAllGroups());
    },
    [
      setFocus,
      getTargetIndex,
      setIsUseRealTimeStartRect,
      setIsUseRealTimeEndRect,
      setAllGroupWidgets,
      getWidgetDataOfAllGroups,
      setRealTimeStartRect,
      setRealTimeEndRect,
      setRealTimeStartPort,
      setRealTimeEndPort,
      dispatch,
    ],
  );

  const handleDrag = useCallback(
    (e: OnDrag, translate: OnDrag['translate']) => {
      const targetIndex = getTargetIndex(e.target);
      if (targetIndex < 0) return;

      lastDelta.current = {
        targetIndex: targetIndex,
        xPx: translate[0],
        yPx: translate[1],
      };

      if (e.inputEvent.shiftKey) {
        const targetPos = getClosestPosByAngle(posList, targetIndex, translate);

        if (targetPos) {
          lastDelta.current.xPx = targetPos.xPx;
          lastDelta.current.yPx = targetPos.yPx;
          e.target.style.transform = `translate(${lastDelta.current.xPx}px, ${lastDelta.current.yPx}px)`;
        }
      }

      setDelta(lastDelta.current);

      // Rendering port connector
      const targetWidgetBaseElement = detectWidgetElementWithProximity(e.clientX, e.clientY);
      const portElement = detectPortElement(e.clientX, e.clientY);

      const targetGroupData = detectNearestGroupElement(e.clientX, e.clientY, allGroupWidgets, true);
      const finalGroupData = !!targetGroupData
        ? targetGroupData
        : !targetWidgetBaseElement && !!targetDragGroupElement
        ? targetDragGroupElement
        : null;

      // Ignore port rendering when trying to connect itself
      const connectedWidgetId = targetIndex === 0 ? endPort?.widgetId : startPort?.widgetId;
      const ignorePortRenderItself = connectedWidgetId && connectedWidgetId === targetWidgetBaseElement?.id;
      if (!portElement) {
        let targetElement = targetWidgetBaseElement && !ignorePortRenderItself ? targetWidgetBaseElement : null;

        // If no port connection then reset realtime rect
        if (targetIndex === 0 && !!setRealTimeStartRect && !!setRealTimeStartPort) {
          setRealTimeStartRect(null);
          setRealTimeStartPort(null);
        } else if (targetIndex > 0 && !!setRealTimeEndRect && !!setRealTimeEndPort) {
          setRealTimeEndRect(null);
          setRealTimeEndPort(null);
        }

        // Responsive widget use the root widget as target.
        if (finalGroupData) {
          setTargetDragGroupElement(finalGroupData);
          renderPort(null, PortTypes.Connector, finalGroupData);
          return;
        } else if (targetElement) {
          const responsiveWidgetElement = getParentWidgetElement(targetElement);
          targetElement = responsiveWidgetElement || targetElement;
        }

        setTargetDragGroupElement(null);

        renderPort(targetElement, PortTypes.Connector);
      } else {
        // There is a rect the line is trying to connect to. set widget's rect for stepped line algorithm.
        let result = calculateWidgetRect(leftPx, topPx, lastDelta.current, posList);
        if (!result) return;

        // Get the responsive widget element if it exists
        const responsiveWidgetElement = !!targetWidgetBaseElement
          ? getParentWidgetElement(targetWidgetBaseElement)
          : null;

        // Set target element id to be the responsive widget id or non responsive widget id
        let targetElementId = !!responsiveWidgetElement
          ? responsiveWidgetElement.id
          : !!targetWidgetBaseElement
          ? targetWidgetBaseElement?.id
          : null;
        let widgetRectData = null;
        if (finalGroupData) {
          //TODO: use group id not widget id
          widgetRectData = getRectOfGroupFromWidgetIdAsRect(finalGroupData?.groupId);
        } else if (targetElementId) {
          widgetRectData = getWidgetBoundingRectData(targetElementId)?.widgetRectData;
        }

        const selectedPort = getSelectedPort();
        if (widgetRectData && !!selectedPort) {
          if (targetIndex === 0 && !!setRealTimeStartRect && !!setRealTimeStartPort) {
            // If start end is being connected, set start rect data
            setRealTimeStartRect(widgetRectData);
            setRealTimeStartPort(selectedPort);
          } else if (
            targetIndex === targetHandlerElmRefs.current.length - 1 &&
            !!setRealTimeEndRect &&
            setRealTimeEndPort
          ) {
            // If end end is being connected, set end rect data
            setRealTimeEndRect(widgetRectData);
            setRealTimeEndPort(selectedPort);
          }
          // Else will not set any rect data
        } else {
          // If no widget rect data, reset realtime rect
          if (targetIndex === 0 && !!setRealTimeStartRect && !!setRealTimeStartPort) {
            setRealTimeStartRect(null);
            setRealTimeStartPort(null);
          } else if (targetIndex > 0 && !!setRealTimeEndRect && !!setRealTimeEndPort) {
            setRealTimeEndRect(null);
            setRealTimeEndPort(null);
          }
        }
      }
    },
    [
      setDelta,
      posList,
      startPort,
      endPort,
      getTargetIndex,
      renderPort,
      targetDragGroupElement,
      setTargetDragGroupElement,
      setRealTimeStartRect,
      setRealTimeEndRect,
      setRealTimeStartPort,
      setRealTimeEndPort,
      getWidgetBoundingRectData,
      getSelectedPort,
      leftPx,
      topPx,
      lastDelta,
      getRectOfGroupFromWidgetIdAsRect,
      allGroupWidgets,
    ],
  );

  const handleDragEnd = useCallback(
    (e: OnDragEnd) => {
      dispatch(setWidgetActionState(WidgetActionState.Default));
      const isStartArrow = activeHandlerIndex === 0;
      const isEndArrow = activeHandlerIndex === posList.length - 1;

      // If the port exists, update the delta value so that the arrow moves to the center of the port
      const selectedPort = getSelectedPort();
      if (selectedPort) {
        if (getWidgetTypeFromId(selectedPort.widgetId) === WidgetType.Group) {
          const groupTargetData = targetDragGroupElement;
          if (!!groupTargetData) {
            const posIndex = isStartArrow ? 0 : isEndArrow ? posList.length - 1 : -1;
            if (posIndex >= 0) {
              // Calculate the port position based on the side.
              const [targetX, targetY] = getPositionMiddleOfSide({
                leftPx: groupTargetData.wrapperPos.leftPx,
                topPx: groupTargetData.wrapperPos.topPx,
                widthPx: groupTargetData.wrapperPos.widthPx,
                heightPx: groupTargetData.wrapperPos.heightPx,
                side: selectedPort.side,
              });

              const offsetX = posList[posIndex].xPx + lastDelta.current.xPx + leftPx - targetX;
              const offsetY = posList[posIndex].yPx + lastDelta.current.yPx + topPx - targetY;

              lastDelta.current.xPx -= offsetX;
              lastDelta.current.yPx -= offsetY;
            }
          }
        } else {
          const targetWidgetElement = document.querySelector(
            getFlowModeWidgetSelector(selectedPort.widgetId),
          ) as HTMLDivElement;
          const matrix = getDOMMatrix(targetWidgetElement);
          const portPos = calculateTargetPoint(targetWidgetElement, matrix, selectedPort);
          const posIndex = isStartArrow ? 0 : isEndArrow ? posList.length - 1 : -1;
          if (posIndex >= 0) {
            const offsetX = posList[posIndex].xPx + lastDelta.current.xPx + leftPx - portPos[0];
            const offsetY = posList[posIndex].yPx + lastDelta.current.yPx + topPx - portPos[1];
            lastDelta.current.xPx -= offsetX;
            lastDelta.current.yPx -= offsetY;
          }
        }
      }

      let result = calculateWidgetRect(leftPx, topPx, lastDelta.current, posList);
      if (!result) return;

      result.rotateDeg = 0; // Reset rotate value

      e.target.style.transform = '';

      // Set the selected port data
      if (result && selectedPort) {
        let linePortData = {
          widgetId: selectedPort.widgetId,
          alignment: selectedPort.alignment,
          side: selectedPort.side,
        };

        if (isStartArrow) result.startPort = linePortData;
        if (isEndArrow) result.endPort = linePortData;
        Mixpanel.track(PORT_NODE_CONNECTED.eventName);
      } else {
        if (isStartArrow) result.startPort = null;
        if (isEndArrow) result.endPort = null;
        // Track disconnect only if previously connected.
        if ((isStartArrow && startPort) || (isEndArrow && endPort)) {
          Mixpanel.track(PORT_NODE_DISCONNECTED.eventName);
        }
      }

      setTimeout(() => {
        setTargetDragGroupElement(null);
        setAllGroupWidgets([]);
      }, 0);
      clearPort();
      updateData(result);
      if (!!setRealTimeStartRect && !!setRealTimeEndRect && !!setRealTimeStartPort && !!setRealTimeEndPort) {
        setRealTimeStartRect(null);
        setRealTimeEndRect(null);
        setRealTimeStartPort(null);
        setRealTimeEndPort(null);
      }

      if (setIsUseRealTimeStartRect) setIsUseRealTimeStartRect(false);
      if (setIsUseRealTimeEndRect) setIsUseRealTimeEndRect(false);

      lastDelta.current = DEFAULT_DELTA_POS;
      setTimeout(() => boundingBox.updateRect(), 0);
    },
    [
      leftPx,
      topPx,
      posList,
      updateData,
      activeHandlerIndex,
      getSelectedPort,
      clearPort,
      boundingBox,
      setIsUseRealTimeStartRect,
      setIsUseRealTimeEndRect,
      setRealTimeStartRect,
      setRealTimeEndRect,
      setRealTimeStartPort,
      setRealTimeEndPort,
      setTargetDragGroupElement,
      setAllGroupWidgets,
      targetDragGroupElement,
      startPort,
      endPort,
      dispatch,
    ],
  );

  useEffect(() => {
    handleFocus(activeHandlerIndex);
  }, [targetElmRefs, activeHandlerIndex]);

  // If useRef is used as an array, re-rendering does not work.
  // Therefore, it triggers a re-render manually when targetElmRefs changed.
  useEffect(() => {
    forceUpdate();
  }, [targetElmRefs, forceUpdate]);

  useEffect(() => {
    if (activeHandlerIndex >= 0) {
      (targetElmRefs.current[activeHandlerIndex]?.current as HTMLDivElement)?.focus();
    } else {
      (targetElmRefs.current[activeHandlerIndex]?.current as HTMLDivElement)?.blur();
    }
  }, [targetElmRefs, activeHandlerIndex]);

  return (
    <>
      {posList.map((pos: Pos, idx: number) => (
        <div key={`${widgetId}-${idx}`}>
          <HandleWrapper
            ref={targetHandlerElmRefs.current[idx]}
            style={{
              left: `${pos.xPx - handlerSize / 2 - HANDLER_PADDING}px`,
              top: `${pos.yPx - handlerSize / 2 - HANDLER_PADDING}px`,
              padding: `${HANDLER_PADDING}px`,
              width: `${handlerSize + HANDLER_PADDING * 2}px`,
              height: `${handlerSize + HANDLER_PADDING * 2}px`,
            }}
          >
            <Handle
              data-testid={'linewidget-customhandler'}
              ref={targetElmRefs.current[idx]}
              data-handler-direction={getCurrentDirection(idx)}
              data-handler-index={idx}
              tabIndex={-1}
              border={`${1 / zoom}px var(--vg-colors-upgrade-500) solid`}
              left={pos.xPx - handlerSize / 2}
              top={pos.yPx - handlerSize / 2}
              width={handlerSize}
              height={handlerSize}
              borderRadius={DEFAULT_CUSTOM_HANDLE_BORDER_RADIUS}
            ></Handle>
          </HandleWrapper>

          {targetElmRefs.current[idx].current ? (
            <DragHandler
              targetRef={targetHandlerElmRefs.current[idx]}
              onDragStart={handleDragStart}
              onDrag={handleDrag}
              onDragEnd={handleDragEnd}
              zoom={zoom}
              /**
               * if there is either startPort or endPort, it means we're in flowmode
               * and we don't want to show smart guide in this case.
               */
              enableSmartGuide={startPort || endPort ? false : true}
            />
          ) : null}
        </div>
      ))}
    </>
  );
};

export default Curved;
